import { useRef, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import Button, { buttonClassName, type ButtonVariant } from '@/components/ui/Button';
import { useFillToBottom } from '@/components/ui/use-fill-to-bottom';

/**
 * Shared empty-state card for views with no domain data yet.
 *
 * ADR-0009: this app ships with no seed data. Every domain row originates
 * from a real ingestion (RBC / Lloyds / Fund recon, COAC upload, KPI upload,
 * etc.) so most views render empty on a fresh session. Each list / dashboard
 * should render this card on the zero-data branch — never fabricate rows,
 * never crash on `Math.max(...empty)` or `arr.reduce(...)` on `[]`.
 *
 * Convention (issue #28):
 * - List views: use `<EmptyState />` in place of the table when the row set
 *   is empty, with a `hint` pointing at the upload surface (e.g.
 *   `{ label: 'Upload an RBC statement', href: '/ops/reconciliation' }`).
 * - Dashboards: use `<EmptyState title="No data uploaded yet" />` per
 *   zero-data section, or wrap the whole page body when the entire view
 *   depends on a single absent source.
 * - Pages that depend on shapes we can't yet reconstruct should wrap the
 *   body and explain in `body` what would populate them.
 * - `actions` are the next steps that the reader can take, for example
 *   "Upload a statement". They show as buttons in one row below the copy.
 *   An action with `href` is a link, and an action with `onClick` is a
 *   button. The default variant is `secondary`.
 * - `fill` centres the empty state in the rest of the page, when it is the
 *   last content of the page. Use it when the empty state stands for the
 *   whole page body.
 *
 * Keep it visually quiet: this is the honest baseline, not a loud CTA.
 */
export interface EmptyStateAction {
    label: string;
    href?: string;
    onClick?: () => void;
    variant?: Extract<ButtonVariant, 'primary' | 'secondary'>;
    icon?: ReactNode;
    testId?: string;
}

export interface EmptyStateProps {
    title: string;
    body?: ReactNode;
    hint?: { label: string; href?: string };
    actions?: readonly EmptyStateAction[];
    fill?: boolean;
    className?: string;
}

export default function EmptyState({
    title,
    body,
    hint,
    actions,
    fill = false,
    className,
}: EmptyStateProps) {
    const ref = useRef<HTMLDivElement>(null);
    const height = useFillToBottom(ref, fill);
    return (
        <div
            ref={ref}
            // A measured height: the rest of the page below the element.
            style={height === undefined ? undefined : { minHeight: height }}
            className={cn('septima-empty-state', className)}
        >
            <h3>{title}</h3>
            {body && <div className="septima-empty-state__body">{body}</div>}
            {hint &&
                (hint.href ? (
                    <Link to={hint.href} className="septima-empty-state__link">
                        {hint.label}
                    </Link>
                ) : (
                    <div className="septima-empty-state__hint">{hint.label}</div>
                ))}
            {actions && actions.length > 0 && (
                <div className="septima-empty-state__actions">
                    {actions.map((action) =>
                        action.href ? (
                            <Link
                                key={action.label}
                                to={action.href}
                                data-testid={action.testId}
                                className={cn(
                                    buttonClassName({ variant: action.variant ?? 'secondary' }),
                                    'gap-1.5',
                                )}
                            >
                                {action.icon}
                                {action.label}
                            </Link>
                        ) : (
                            <Button
                                key={action.label}
                                variant={action.variant ?? 'secondary'}
                                icon={action.icon}
                                onClick={action.onClick}
                                data-testid={action.testId}
                            >
                                {action.label}
                            </Button>
                        ),
                    )}
                </div>
            )}
        </div>
    );
}
